import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { ReactElement, SyntheticEvent, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { createFeature, getFeaturesDetailsById, updateFeature } from '../../redux/slices/features/featuresSlice';
import { useDispatch } from '../../redux/store';
import { TextField } from '../../styled/inputs';
import { GetValidationErrors } from '../../tool/validation';

interface IProp {
  onClose: (options?: { redirect?: string; edit?: boolean }) => void;
  open: boolean;
  featureId?: string;
}

interface Values {
  name: string;
}

const schema = yup.object().shape({
  name: yup.string().required('Required field'),
});

export const FeatureForm = ({ onClose, open, featureId }: IProp): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const details = useSelector(getFeaturesDetailsById(featureId));

  const submit = async (values: Values, { setSubmitting, setErrors }: FormikHelpers<Values>) => {
    try {
      setSubmitting(true);
      if (details?.id) {
        await dispatch(updateFeature({ featureId: details?.id, feature: values }));
        onClose();
      } else {
        const response = await dispatch(createFeature(values)).unwrap();
        onClose({ redirect: `/admin/vertical-markets/features/${response.data.result?.id}/details` });
      }
      // eslint-disable-next-line
    } catch (e: any) {
      setErrors(GetValidationErrors(e.response.status, navigate, e.response.data.messages));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>, reason?: string) => {
      if (reason !== 'backdropClick') {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
      <Formik
        validationSchema={schema}
        onSubmit={submit}
        initialValues={{
          name: details?.name ?? '',
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          // isValid,
          errors,
          isSubmitting,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogTitle>{details?.id ? 'Edit' : 'Add'} Feature</DialogTitle>
            <DialogContent>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <TextField
                  name="name"
                  label="Name"
                  error={Boolean(touched.name && errors.name)}
                  fullWidth
                  value={values.name}
                  helperText={touched.name && errors.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  variant="outlined"
                  my={2}
                  disabled={isSubmitting}
                  required
                />
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 6, pb: 6 }}>
              <Button onClick={handleClose} color="primary">
                Cancel
              </Button>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                {details?.id ? 'Save' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};
